import { listNations, PLAYER_COLOURS } from '@brig/nations';
import type { Side } from '@brig/rules-engine';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { Colours, Nations } from '../game/history.ts';
import { currentLocale } from '../i18n/index.ts';
import { NationFlag } from '../nations/NationFlag.tsx';
import * as styles from './FleetPicker.css.ts';

const SIDES: readonly Side[] = ['red', 'blue'];

/**
 * Each fleet's nation and colour for a new game: the player's own (the side that moves first,
 * the creator's online and against the computer) and the opponent's. What the other fleet
 * already flies is not on offer: the two must differ.
 */
export function FleetPicker({
  nations,
  onNations,
  colours,
  onColours,
}: {
  nations: Nations;
  onNations: (n: Nations) => void;
  colours: Colours;
  onColours: (c: Colours) => void;
}) {
  const { t } = useTranslation();
  const name = useId();
  const locale = currentLocale();
  return (
    <div className={styles.fleets}>
      {SIDES.map((side) => {
        const other = side === 'red' ? 'blue' : 'red';
        return (
          <fieldset key={side} className={styles.fleet}>
            <legend className={styles.legend}>{t(`newGame.fleet.${side}`)}</legend>
            <div className={styles.nations}>
              {listNations().map((n) => (
                <label key={n.id} className={styles.nation} title={n.names[locale]}>
                  <input
                    type="radio"
                    className={styles.radio}
                    name={`${name}-${side}-nation`}
                    value={n.id}
                    checked={nations[side] === n.id}
                    disabled={nations[other] === n.id}
                    onChange={() => {
                      onNations({ ...nations, [side]: n.id });
                    }}
                  />
                  <NationFlag nation={n.id} height={18} decorative />
                  <span className={styles.nationName}>{n.names[locale]}</span>
                </label>
              ))}
            </div>
            <div className={styles.colours} role="radiogroup" aria-label={t('newGame.fleet.colour')}>
              {PLAYER_COLOURS.map((c) => (
                <label key={c.id} className={styles.colour} title={c.names[locale]}>
                  <input
                    type="radio"
                    className={styles.radio}
                    name={`${name}-${side}-colour`}
                    value={c.id}
                    checked={colours[side] === c.id}
                    disabled={colours[other] === c.id}
                    aria-label={c.names[locale]}
                    onChange={() => {
                      onColours({ ...colours, [side]: c.id });
                    }}
                  />
                  <span className={styles.swatch} style={{ background: c.hex }} />
                </label>
              ))}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
